import { Block } from "app/shared/components/blocks/block";
import ImageCard from "app/shared/components/card/image-card/ImageCard";
import { Constant } from "app/shared/constant/constant";
import { CARDITEM } from "app/shared/services/services-model.type";
import { spacing } from "app/shared/styles/spacing";
import * as React from "react";
import { Pressable, StyleSheet } from "react-native";
import { useTheme, Paragraph } from "react-native-paper";
import Animated from "react-native-reanimated";
import { useSelector } from "react-redux";
import { getCardNumber, getExpiry, getMemberSince, LIST_CARD_PROTOTYPE } from "../../card-utils/card-utils";
import { getCardFullNumSelector } from "../../reducer/selectors";

interface WrappingCardProps {
  card?: CARDITEM;
  style: any;
  widthCard: number;
  onPress: Function;
}

const WrappingCard = (props: WrappingCardProps) => {
  const { card, widthCard, style, onPress } = props;
  const { colors, fonts } = useTheme();

  const cardFullNumStore = useSelector(getCardFullNumSelector);
  const [cardNum, setCardNum] = React.useState(card?.cardId);

  const [heightCard, setHeightCard] = React.useState<number>((220 * 220) / 350);

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        textStyle: { ...fonts.card, color: colors.whiteText, zIndex: 999 },
        textStyleNum: {
          fontSize: 13,
          letterSpacing: 3.25,
          backgroundColor: "transparent",
          flex: 1,
          textAlign: "center",
          textShadowColor: "#212121",
          textShadowOffset: { width: -0.75, height: -0.5 },
          textShadowRadius: 0.5,
        },
        labelValid: {
          ...fonts.semibold,
          fontSize: 6,
          textAlign: "center",
          lineHeight: 12,
          minWidth: 48,
        },
        textStyleValid: {
          fontSize: 10,
          textAlign: "center",
          textShadowColor: "black",
          textShadowOffset: { width: -0.5, height: -0.5 },
          textShadowRadius: 1,
          lineHeight: 12,
          letterSpacing: 2.5,
        },
        textStyleName: {
          fontSize: 13,
          letterSpacing: 2,
          backgroundColor: "transparent",
          textShadowColor: "#212121",
          textShadowOffset: { width: -0.75, height: -0.5 },
          textShadowRadius: 0.5,
        },
      }),
    [colors.whiteText, fonts],
  );

  const getImageNameByCardClass = (_item?: CARDITEM) => {
    let imageName = "card_credit_class";
    const filter = LIST_CARD_PROTOTYPE.filter((prototype) => {
      return _item?.cardClass === prototype.KEY;
    });
    if (filter.length) imageName = filter[0].IMAGE.length ? filter[0].IMAGE : "card_credit_class";
    return imageName;
  };

  const renderCardNum = (
    <Block flex={1} w={"100%"} absolute row spaced absBottom={heightCard * 0.32} ph={widthCard * 0.07}>
      <Paragraph style={[styles.textStyle, styles.textStyleNum]}>{getCardNumber(cardNum).slice(0, 4)}</Paragraph>
      <Paragraph style={[styles.textStyle, styles.textStyleNum]}>{getCardNumber(cardNum).slice(4, 8)}</Paragraph>
      <Paragraph style={[styles.textStyle, styles.textStyleNum]}>{getCardNumber(cardNum).slice(8, 12)}</Paragraph>
      <Paragraph style={[styles.textStyle, styles.textStyleNum]}>{getCardNumber(cardNum).slice(12, 16)}</Paragraph>
    </Block>
  );

  const renderValidDebit = (
    <Block absolute row spaced absBottom={heightCard * 0.16} absLeft={widthCard * 0.11}>
      <Block flex={1} middle center>
        <Paragraph style={[styles.textStyle, styles.labelValid]}>VALID FROM</Paragraph>
        <Paragraph style={[styles.textStyle, styles.textStyleValid]}>{getMemberSince(card?.issueDate)}</Paragraph>
      </Block>
      <Block flex={1} middle center ml={spacing.galactic + spacing.huge}>
        <Paragraph style={[styles.textStyle, styles.labelValid]}>UNTIL END</Paragraph>
        <Paragraph style={[styles.textStyle, styles.textStyleValid]}>{getExpiry(card?.expireDate)}</Paragraph>
      </Block>
    </Block>
  );

  const renderValidOther = (
    <Block absolute row spaced absBottom={heightCard * 0.16} absLeft={widthCard * 0.11}>
      <Block flex={1} middle center>
        <Paragraph style={[styles.textStyle, styles.labelValid]}>VALID THRU</Paragraph>
        <Paragraph style={[styles.textStyle, styles.textStyleValid]}>{getExpiry(card?.expireDate)}</Paragraph>
      </Block>
    </Block>
  );

  const renderCard = (
    <Block center middle>
      {renderCardNum}
      {card?.cardType === Constant.CARD.CARD_TYPE.DEBIT || card?.cardType === Constant.CARD.CARD_TYPE.DEBIT_MASTER
        ? renderValidDebit
        : renderValidOther}
      <Block absolute middle absBottom={heightCard * 0.04} absLeft={widthCard * 0.095}>
        <Paragraph style={[styles.textStyle, styles.textStyleName]}>{card?.embossName}</Paragraph>
      </Block>
    </Block>
  );

  React.useEffect(() => {
    setHeightCard((widthCard * 220) / 350);
  }, [widthCard]);

  React.useEffect(() => {
    if (cardFullNumStore?.cardID === card?.cardId) {
      setCardNum(cardFullNumStore?.cardNum);
    } else {
      setCardNum(card?.cardNum);
    }
  }, [card?.cardId, card?.cardNum, cardFullNumStore]);

  return (
    <Animated.View style={style}>
      <Pressable onPress={() => onPress()}>
        <ImageCard name={getImageNameByCardClass(card)} width={widthCard} aspectRatio={220 / 350} />
        {renderCard}
      </Pressable>
    </Animated.View>
  );
};

export default WrappingCard;
